/**
 * TopicB2BReaderContractCard — the "B2B reader contract" card surfaced in
 * TopicDetailPanel's Overview tab.
 *
 * Surfaces the per-topic B2B axes that were already on the data but invisible:
 *   - audienceFit[]     — per-audience structured fits with strength + source
 *   - appliesToAllAudiences (+ rationale) — explicit universal flag
 *   - targetAudienceIds — legacy flat list for backwards compat
 *   - targetDmuRoles, targetForceTags, targetPyramidLevels, buyingSequencePosition
 *   - dataUsage citations specific to audienceContentBriefs
 *
 * If none of the B2B fields are set, the card renders nothing (B2C topics
 * stay clean). For B2B topics the validator + sprint planner already enforce
 * these fields; this UI is purely the "show what's there" layer.
 */

import React from 'react';
import {
  Paper,
  Stack,
  Typography,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import type { ContentTopic, AudienceFit } from '@modules/content-plan/types';

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export interface TopicB2BReaderContractCardProps {
  topic: ContentTopic;
}

const FIT_COLOR: Record<string, 'success' | 'info' | 'warning'> = {
  strong: 'success',
  medium: 'info',
  weak: 'warning',
};

const SOURCE_COLOR: Record<string, 'primary' | 'default'> = {
  user_confirmed: 'primary',
  ai_verified: 'default',
};

export const TopicB2BReaderContractCard: React.FC<TopicB2BReaderContractCardProps> = ({ topic }) => {
  const audienceFit = asArray<AudienceFit>((topic as any).audienceFit);
  const appliesToAll = (topic as any).appliesToAllAudiences === true;
  const appliesToAllRationale = (topic as any).appliesToAllAudiencesRationale as string | undefined;
  const targetAudienceIds = asArray<string>((topic as any).targetAudienceIds);
  const targetDmuRoles = asArray<string>((topic as any).targetDmuRoles);
  const targetForceTags = asArray<string>((topic as any).targetForceTags);
  const targetPyramidLevels = asArray<string>((topic as any).targetPyramidLevels);
  const buyingSequencePosition = (topic as any).buyingSequencePosition as string | undefined;
  const dataUsageItems = asArray<{ sourcePath?: string; justification?: string; value?: string }>(
    (topic as any).dataUsage?.items,
  );

  const audienceCitations = dataUsageItems.filter((it) =>
    it.sourcePath?.includes('audienceContentBriefs'),
  );

  // Hide the card entirely for B2C topics — no B2B axes set at all.
  const hasAnyB2B =
    audienceFit.length > 0
    || appliesToAll
    || targetAudienceIds.length > 0
    || targetDmuRoles.length > 0
    || targetForceTags.length > 0
    || targetPyramidLevels.length > 0
    || Boolean(buyingSequencePosition)
    || audienceCitations.length > 0;
  if (!hasAnyB2B) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="topic-b2b-reader-contract-card">
      <Typography variant="subtitle2" gutterBottom>
        B2B reader contract
      </Typography>

      {/* audienceFit[] — primary representation */}
      {audienceFit.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Audience fits
          </Typography>
          <Stack spacing={0.75}>
            {audienceFit.map((fit, i) => {
              const ref = (fit as any).audienceBriefRef ?? {};
              const fitKind = (fit as any).fit as string | undefined;
              const source = (fit as any).source as string | undefined;
              return (
                <Box
                  key={`${ref.analysisId ?? 'a'}-${ref.audienceId ?? i}`}
                  sx={{ p: 0.75, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                      {ref.labelSnapshot ?? ref.audienceId ?? 'audience'}
                    </Typography>
                    {fitKind && (
                      <Chip
                        size="small"
                        color={FIT_COLOR[fitKind] ?? 'default'}
                        label={`fit: ${fitKind}`}
                      />
                    )}
                    {source && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={SOURCE_COLOR[source] ?? 'default'}
                        label={source}
                      />
                    )}
                  </Stack>
                  {(fit as any).rationale && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {(fit as any).rationale}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* applies-to-all explicit flag */}
      {appliesToAll && (
        <Box sx={{ mb: 1.5 }}>
          <Chip size="small" color="primary" label="Applies to all audiences" />
          {appliesToAllRationale && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {appliesToAllRationale}
            </Typography>
          )}
        </Box>
      )}

      {/* Legacy / supporting flat lists */}
      {(targetAudienceIds.length > 0
        || targetDmuRoles.length > 0
        || targetForceTags.length > 0
        || targetPyramidLevels.length > 0
        || buyingSequencePosition) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {targetAudienceIds.map((id) => (
              <Chip key={`aud-${id}`} size="small" variant="outlined" label={`audience: ${id}`} />
            ))}
            {targetDmuRoles.map((r) => (
              <Chip key={`dmu-${r}`} size="small" variant="outlined" label={`role: ${r}`} />
            ))}
            {targetForceTags.map((f) => (
              <Chip key={`force-${f}`} size="small" variant="outlined" label={`force: ${f}`} />
            ))}
            {targetPyramidLevels.map((lvl) => (
              <Chip key={`pyr-${lvl}`} size="small" variant="outlined" label={`pyramid: ${lvl}`} />
            ))}
            {buyingSequencePosition && (
              <Chip size="small" variant="outlined" color="secondary" label={`sequence: ${buyingSequencePosition}`} />
            )}
          </Stack>
        </>
      )}

      {/* audienceContentBriefs citations only */}
      {audienceCitations.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Brief citations
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {audienceCitations.slice(0, 6).map((c, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={c.sourcePath ?? `citation ${i + 1}`}
                title={c.justification ?? c.value ?? ''}
              />
            ))}
            {audienceCitations.length > 6 && (
              <Chip size="small" label={`+${audienceCitations.length - 6} more`} />
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default TopicB2BReaderContractCard;
